import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import TaxComponentForm from './TaxComponentForm';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TaxComponent, TaxComponentCreate, TaxComponentUpdate } from '@/app/types/taxComponent';
import { listTaxComponents, createTaxComponent, updateTaxComponent, deleteTaxComponent } from '@/app/services/taxComponentService';
import { ListQueryParams } from '@/app/types/api';
import { useAuthStore } from '@/app/store/authStore';

interface TaxComponentManagementProps {
  // Props might be needed if triggered from another component
}

const TaxComponentManagement: React.FC<TaxComponentManagementProps> = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentComponent, setCurrentComponent] = useState<TaxComponent | null>(null);

  const user = useAuthStore((state) => state.user);
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user]);

  // Query to fetch tax components
  const {
    data: componentsResponse,
    isLoading: isComponentsLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['taxComponents', userOrgId],
    queryFn: () => {
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      const params: ListQueryParams = { skip: 0, limit: 500, sort_by: 'name', sort_dir: 'asc' }; // Fetch many for management UI
      return listTaxComponents(params, userOrgId);
    },
    enabled: !!userOrgId,
  });

  const components = componentsResponse?.items ?? [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: TaxComponentCreate) => {
      if (!userOrgId) throw new Error("Organization ID missing");
      return createTaxComponent(data);
    },
    onSuccess: () => {
      toast.success('Tax component created successfully');
      queryClient.invalidateQueries({ queryKey: ['taxComponents', userOrgId] });
      setIsFormOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create tax component: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TaxComponentUpdate }) => updateTaxComponent(id, data),
    onSuccess: () => {
      toast.success('Tax component updated successfully');
      queryClient.invalidateQueries({ queryKey: ['taxComponents', userOrgId] });
      setIsFormOpen(false);
      setCurrentComponent(null);
    },
    onError: (error) => {
      toast.error(`Failed to update tax component: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteTaxComponent(id),
    onSuccess: () => {
      toast.success('Tax component deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['taxComponents', userOrgId] });
      queryClient.invalidateQueries({ queryKey: ['taxrates', userOrgId] }); // Invalidate tax rates too
      setIsDeleteDialogOpen(false);
      setCurrentComponent(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete tax component: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Handlers
  const handleAddClick = () => {
    setIsEditMode(false);
    setCurrentComponent(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (component: TaxComponent) => {
    if (component.is_system || component.organization_id === null) {
        toast.error("Cannot edit global or system tax components.");
        return;
    }
    setIsEditMode(true);
    setCurrentComponent(component);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (component: TaxComponent) => {
     if (component.is_system || component.organization_id === null) {
        toast.error("Cannot delete global or system tax components.");
        return;
    }
    setCurrentComponent(component);
    setIsDeleteDialogOpen(true);
  };

  const handleFormSubmit = (data: TaxComponentCreate | TaxComponentUpdate) => {
    if (isEditMode && currentComponent) {
      updateMutation.mutate({ id: currentComponent.id, data: data as TaxComponentUpdate });
    } else {
      createMutation.mutate(data as TaxComponentCreate);
    }
  };

  const handleDeleteConfirm = () => {
    if (currentComponent) {
      deleteMutation.mutate(currentComponent.id);
    }
  };

   if (isError) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive flex items-center gap-2">
             <AlertTriangle /> Error Loading Tax Components
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
             {error instanceof Error ? error.message : 'An unknown error occurred.'}
          </p>
        </CardContent>
      </Card>
    );
  }


  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <div>
            <CardTitle>Manage Tax Components</CardTitle>
            <CardDescription>
                Define the individual components (like CGST, SGST) used in your tax rates.
            </CardDescription>
        </div>
        <Button onClick={handleAddClick} size="sm">
          <Plus className="mr-2 h-4 w-4" /> Add Component
        </Button>
      </CardHeader>
      <CardContent>
        {isComponentsLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : components.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            No custom tax components found. System components (if any) are used by default.
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {components.map((component) => (
              <div key={component.id} className="flex items-center gap-1 border rounded-lg p-2 pr-1 bg-secondary/30">
                 <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                             <Badge variant={component.organization_id === null ? 'outline' : 'secondary'} className="font-medium">
                                {component.name} ({component.code})
                             </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                           <p>{component.organization_id === null ? 'Global/System Component' : 'Organization Component'}</p>
                           {component.description && <p className="text-xs text-muted-foreground mt-1">{component.description}</p>}
                        </TooltipContent>
                    </Tooltip>
                 </TooltipProvider>
                {/* Only allow editing/deleting non-global/non-system components */}
                {(component.organization_id !== null && !component.is_system) && (
                    <>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleEditClick(component)}
                            disabled={updateMutation.isPending}
                        >
                            <Edit className="h-3.5 w-3.5" />
                            <span className="sr-only">Edit</span>
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteClick(component)}
                            disabled={deleteMutation.isPending}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                            <span className="sr-only">Delete</span>
                        </Button>
                    </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create/Edit Form */}
      <TaxComponentForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSubmit={handleFormSubmit}
        defaultValues={currentComponent ?? undefined}
        isLoading={createMutation.isPending || updateMutation.isPending}
        mode={isEditMode ? 'edit' : 'create'}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the tax component &quot;{currentComponent?.name}&quot;. It cannot be undone and might affect existing Tax Rates using it if deletion isn't restricted by the backend.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default TaxComponentManagement; 